import React from 'react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { VideoSourceSection } from './VideoSourceSection';
import { VideoThumbnailSection } from './VideoThumbnailSection';
import { ButtonsEditor, type ButtonData } from '../shared/ButtonsEditor';

interface VideoData {
  title?: string;
  subtitle?: string;
  description?: string;
  source?: {
    sourceType?: 'upload' | 'youtube' | 'vimeo';
    url: string;
    embedUrl?: string;
    cloudfrontUrl?: string;
    mediaId?: string;
    mimeType?: string;
    duration?: number;
  };
  thumbnail?: {
    url: string;
    cloudfrontUrl?: string;
    mediaId?: string;
    alt?: string;
  };
  playback?: {
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean;
    controls?: boolean;
    allowFullscreen?: boolean;
    defaultVolume?: number;
  };
  aspectRatio?: string;
  width?: string;
  alignment?: string;
  borderRadius?: string;
  buttons?: ButtonData[];
  buttonsPosition?: string;
  showMetadata?: boolean;
  showTimestamp?: boolean;
  customColors?: {
    background?: string;
    title?: string;
    subtitle?: string;
    text?: string;
    buttonBackground?: string;
    buttonText?: string;
  };
}

interface VideoEditorProps {
  formData: VideoData;
  setFormData: (data: VideoData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image' | 'video', assetType: string) => void;
  currentPageComponents?: Component[];
}

export const VideoEditor: React.FC<VideoEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData || {};
  const themeColors = useCustomColors();

  const openVideoUpload = () => {
    onOpenMediaUpload('video', 'video');
  };

  const openThumbnailUpload = () => {
    onOpenMediaUpload('image', 'thumbnail');
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('video', themeColors);

  return (
    <div className="space-y-8">
      {/* Header Content */}
      <HeaderSection data={data} setFormData={setFormData} />

      {/* Video Source */}
      <VideoSourceSection data={data} setFormData={setFormData} openVideoUpload={openVideoUpload} />

      {/* Thumbnail */}
      <VideoThumbnailSection
        data={data}
        setFormData={setFormData}
        openThumbnailUpload={openThumbnailUpload}
      />

      {/* Playback Settings */}
      <PlaybackSection data={data} setFormData={setFormData} />

      {/* Layout Settings */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={(data.buttons || []) as ButtonData[]}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        showSize
        showGhostStyle
      />

      {/* Button Position (video-specific) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Button Position</label>
        <select
          value={data.buttonsPosition || 'below'}
          onChange={e => setFormData({ ...data, buttonsPosition: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="below">Below Video</option>
          <option value="overlay">Overlay on Video</option>
          <option value="beside">Beside Video</option>
        </select>
      </div>

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize video section colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface HeaderSectionProps {
  data: VideoData;
  setFormData: (data: VideoData) => void;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Header Content</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
        <input
          type="text"
          value={data.subtitle || ''}
          onChange={e => setFormData({ ...data, subtitle: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Watch this comprehensive demo..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.title || ''}
          onChange={e => setFormData({ ...data, title: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Discover Our Product in Action"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={data.description || ''}
          onChange={e => setFormData({ ...data, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="This video showcases the key features..."
        />
      </div>
    </div>
  </div>
);

interface PlaybackSectionProps {
  data: VideoData;
  setFormData: (data: VideoData) => void;
}

const PlaybackSection: React.FC<PlaybackSectionProps> = ({ data, setFormData }) => (
  <div>
    <h3 className="text-lg font-medium text-gray-900 mb-4">Playback Settings</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.playback?.autoplay || false}
            onChange={e =>
              setFormData({ ...data, playback: { ...data.playback, autoplay: e.target.checked } })
            }
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Autoplay</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.playback?.muted !== false}
            onChange={e =>
              setFormData({ ...data, playback: { ...data.playback, muted: e.target.checked } })
            }
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Muted</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.playback?.loop || false}
            onChange={e =>
              setFormData({ ...data, playback: { ...data.playback, loop: e.target.checked } })
            }
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Loop</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.playback?.controls !== false}
            onChange={e =>
              setFormData({ ...data, playback: { ...data.playback, controls: e.target.checked } })
            }
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Show Controls</span>
        </label>
      </div>

      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.playback?.allowFullscreen !== false}
            onChange={e =>
              setFormData({
                ...data,
                playback: { ...data.playback, allowFullscreen: e.target.checked },
              })
            }
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Allow Fullscreen</span>
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.showMetadata || false}
            onChange={e => setFormData({ ...data, showMetadata: e.target.checked })}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-700">Show Metadata</span>
        </label>

        {data.showMetadata && (
          <label className="flex items-center ml-6">
            <input
              type="checkbox"
              checked={data.showTimestamp || false}
              onChange={e => setFormData({ ...data, showTimestamp: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700">Show Duration</span>
          </label>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Default Volume</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={data.playback?.defaultVolume || 0.8}
            onChange={e =>
              setFormData({
                ...data,
                playback: { ...data.playback, defaultVolume: parseFloat(e.target.value) },
              })
            }
            className="w-full"
          />
          <div className="text-xs text-gray-500 mt-1">
            {Math.round((data.playback?.defaultVolume || 0.8) * 100)}%
          </div>
        </div>
      </div>
    </div>
  </div>
);

interface LayoutSectionProps {
  data: VideoData;
  setFormData: (data: VideoData) => void;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({ data, setFormData }) => {
  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Layout & Styling</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Width</label>
          <select
            value={data.width || 'container'}
            onChange={e => setFormData({ ...data, width: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="container">Container</option>
            <option value="full">Full Width</option>
            <option value="fixed">Fixed Width</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Border Radius</label>
          <select
            value={data.borderRadius || 'medium'}
            onChange={e => setFormData({ ...data, borderRadius: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="none">None</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
      </div>
    </div>
  );
};
